<template>
  <f7-actions
    v-model:opened="opened"
    grid
    class="parts-item-secondary-actions"
  >
    <f7-actions-group>
      <f7-actions-button
        v-if="shouldShowChangeRoute"
        :disabled="!isAllowedToDoRouteOperation"
        @click="onRegisterRouteChange"
      >
        <template #media>
          <UCSvgIcon name="fa6-solid-car" />
        </template>
        <span>Cambiar trayecto</span>
      </f7-actions-button>

      <template v-if="!isPartFinished">
        <f7-actions-button
          v-if="isUserResponsible"
          @click="openWarehousesPopup"
        >
          <template #media>
            <UCSvgIcon name="fa6-solid-warehouse" />
          </template>
          <span>
            {{ $t('common.warehouse') }}
          </span>
        </f7-actions-button>

        <f7-actions-button @click="isPartArticlesPopupOpened = true">
          <template #media>
            <UCSvgIcon name="fa6-solid-briefcase" />
          </template>
          <span>
            {{ $t('common.articles') }}
          </span>
        </f7-actions-button>

        <f7-actions-button
          v-if="workPart.TrabajosEnDomicilio"
          @click="openMap"
        >
          <template #media>
            <UCSvgIcon name="fa6-solid-map" />
          </template>
          <span>
            {{ $t('common.map') }}
          </span>
        </f7-actions-button>
      </template>

      <template v-else>
        <f7-actions-button
          @click="navigateToSignPage(
            workPart.Id_ParteProduccion,
            SignTypes.PART,
          )"
        >
          <template #media>
            <UCSvgIcon name="fa6-solid-briefcase" />
          </template>
          <span>Firmar parte</span>
          <span
            v-if="workPart.FirmaParte"
            class="signature-icon"
          >
            <UCSvgIcon name="fa6-solid-circle-check" />
          </span>
        </f7-actions-button>

        <f7-actions-button
          @click="navigateToSignPage(
            workPart.Id_ParteProduccion,
            SignTypes.DELIVERY_NOTE_NO_SIGN,
          )"
        >
          <template #media>
            <UCSvgIcon name="fa6-solid-file-invoice" />
          </template>
          <span>Albarán</span>
        </f7-actions-button>

        <f7-actions-button
          @click="navigateToSignPage(
            workPart.Id_ParteProduccion,
            SignTypes.DELIVERY_NOTE,
          )"
        >
          <template #media>
            <UCSvgIcon name="fa6-solid-file-invoice" />
          </template>
          <span>Firmar albarán</span>
          <span
            v-if="workPart.FirmaAlbaran"
            class="signature-icon"
          >
            <UCSvgIcon name="fa6-solid-circle-check" />
          </span>
        </f7-actions-button>
      </template>
    </f7-actions-group>

    <!-- Popup: Part Warehouses -->
    <PCPartWarehousesPopup
      ref="warehousesPopup"
      @added="handleWarehouseArticleAdded"
    />

    <!-- Popup: Expected and Real Consumptions -->
    <PCPartArticlesPopup
      v-model:opened="isPartArticlesPopupOpened"
      :work-part-id="workPart.Id_ParteProduccion"
    />

    <!-- Popup: Map Popup -->
    <PCMapPopup
      v-if="workPart.Latitud !== null && workPart.Longitud !== null"
      v-model:opened="isMapPopupOpened"
      :latitude="workPart.Latitud"
      :longitude="workPart.Longitud"
    />
  </f7-actions>
</template>

<script setup>
// @ts-check

import { computed, ref, shallowRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useRepairParts } from '@/composables/useRepairParts';
import { useRepairPartsStore } from '@/js/stores/repairParts';
import { useParamRef } from '@erpagroapp/params';
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';

import { SignTypes } from '@/js/enums/externalRepairParts';
import { callRegisterRouteChange } from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';

import PCMapPopup from '@/components/common/PCMapPopup.vue';
import UCSvgIcon from '@/components/common/UCSvgIcon.vue';
import PCPartWarehousesPopup from '../PCPartWarehousesPopup.vue';
import PCPartArticlesPopup from '../PCPartArticlesPopup.vue';

/** @import { PropType, Ref, ShallowRef } from 'vue' */
/** @import { WorkPartItem } from '@/js/stores/repairParts' */

const props = defineProps({
  workPart: {
    /** @type {PropType<WorkPartItem>} */
    type: Object,
    default: null,
  },
});

const opened = defineModel('opened', {
  type: Boolean,
  default: false,
});

const isPartArticlesPopupOpened = ref(false);

const {
  isUserResponsibleWorkPart,
  loadWorkParts,
  navigateToSignPage,
  navigateToPartDetails,
  isAllowedToDoRouteOperation,
} = useRepairParts();

const {
  worker,
} = storeToRefs(useRepairPartsStore());

const partStatusRouteStarted = useParamRef('EstadoParteReapracionInicioDesplazamiento');
const partStatusStarted = useParamRef('EstadoParteReparacionEnCliente');

const isPartFinished = computed(() => props.workPart.FechaFinalizacionOrden !== null);
const isUserResponsible = computed(() => isUserResponsibleWorkPart(props.workPart.Id_TrabajadorPropietario));

function useRouteChange() {
  const shouldShowRouteButtons = computed(() => !isPartFinished.value
    && !!props.workPart.TrabajosEnDomicilio);

  const shouldShowChangeRoute = computed(() => shouldShowRouteButtons.value
    && isUserResponsible.value
    && props.workPart.Codigo_ParteProduccionEstado === partStatusRouteStarted.value
    && props.workPart.NumeroTrabajadoresActivos > 0);

  /**
   * Registers the route change for the work part.
   * @param {Record<string, any>} repairPartData
   * @returns {Promise<void>}
   */
  async function registerRouteChange(repairPartData) {
    HiDialog.Preloader(true, 'Cambiando el trayecto...');

    try {
      const { data } = await callRegisterRouteChange(repairPartData);

      if (data.Valor === true) {
        HiNotification.Toast('El trayecto se ha cambiado correctamente');

        // Refresh the parts
        loadWorkParts();
      } else {
        HiNotification.Toast(data.Mensaje || 'No se ha podido cambiar el trayecto');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se ha podido cambiar el trayecto');
    } finally {
      HiDialog.Preloader(false);
    }
  }

  /**
   * Handles when user clicks on the 'Change Route' button.
   * @returns {void}
   */
  function onRegisterRouteChange() {
    if (!isAllowedToDoRouteOperation.value) {
      HiNotification.Toast('No está permitido realizar operaciones de trayecto de otros días');
      return;
    }

    const repairPartData = {
      ParteProduccion: {
        Id: props.workPart.Id_ParteProduccion,
      },
      Trabajadores: [worker.value],
    };

    registerRouteChange(repairPartData);
  }

  return {
    shouldShowChangeRoute,
    onRegisterRouteChange,
  };
}

function useWarehousePopup() {
  /** @type {ShallowRef<InstanceType<typeof PCPartWarehousesPopup> | null>} */
  const warehousesPopup = shallowRef(null);

  /**
   * Opens the warehouse popup.
   * @returns {void}
   */
  function openWarehousesPopup() {
    warehousesPopup.value?.openPopup(props.workPart);
  }

  /**
   * Handler for when an article is added to the work part.
   * @param {WorkPartItem} workPartReceived - The received work part.
   */
  function handleWarehouseArticleAdded(workPartReceived) {
    if (workPartReceived.Codigo_ParteProduccionEstado === partStatusStarted.value) {
      navigateToPartDetails(workPartReceived.Id_ParteProduccion);
    }
  }

  return {
    warehousesPopup,
    openWarehousesPopup,
    handleWarehouseArticleAdded,
  };
}

function useMapPopup() {
  /** @type {Ref<boolean>} */
  const isMapPopupOpened = ref(false);

  function openMap() {
    if (props.workPart.Latitud === null || props.workPart.Longitud === null) {
      HiNotification.Toast('El parte no tiene establecidas las coordenadas');
      return;
    }

    isMapPopupOpened.value = true;
  }

  return {
    isMapPopupOpened,
    openMap,
  };
}

const {
  shouldShowChangeRoute,
  onRegisterRouteChange,
} = useRouteChange();

const {
  warehousesPopup,
  openWarehousesPopup,
  handleWarehouseArticleAdded,
} = useWarehousePopup();

const {
  isMapPopupOpened,
  openMap,
} = useMapPopup();
</script>

<style scoped lang="scss">
.parts-item-secondary-actions {
  :deep(.actions-group) {
    .actions-button-media {
      justify-content: center;

      svg {
        font-size: 48px;
      }
    }

    .actions-button {
      flex: 0 0 33.33%;
    }
  }

  .signature-icon {
    vertical-align: middle;
    padding-left: 4px;
  }
}
</style>
